import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, CheckCircle, AlertCircle, XCircle, Activity } from 'lucide-react';

const StatusPage = () => {
  const services = [
    { name: 'Web Application', status: 'operational', uptime: '99.99%' },
    { name: 'API Services', status: 'operational', uptime: '99.98%' },
    { name: 'Database', status: 'operational', uptime: '99.99%' },
    { name: 'Authentication', status: 'operational', uptime: '100%' },
    { name: 'CDN', status: 'operational', uptime: '100%' },
    { name: 'Email Service', status: 'maintenance', uptime: '99.95%' }
  ];

  const incidents = [
    {
      date: '2025-09-20',
      title: 'Scheduled Maintenance',
      status: 'resolved',
      description: 'Database optimization completed successfully.'
    },
    {
      date: '2025-09-18',
      title: 'API Latency',
      status: 'resolved',
      description: 'Brief increase in API response times. Issue resolved within 15 minutes.'
    },
    {
      date: '2025-09-15',
      title: 'Login Issues',
      status: 'resolved',
      description: 'Some users experienced login delays. Fixed by scaling authentication service.'
    }
  ];

  const getStatusIcon = (status) => {
    switch(status) {
      case 'operational':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'maintenance':
        return <AlertCircle className="w-5 h-5 text-yellow-500" />;
      case 'outage':
        return <XCircle className="w-5 h-5 text-red-500" />;
      default:
        return <CheckCircle className="w-5 h-5 text-green-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'operational':
        return 'text-green-600';
      case 'maintenance':
        return 'text-yellow-600';
      case 'outage':
        return 'text-red-600';
      default:
        return 'text-green-600';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-white">
      <div className="max-w-6xl mx-auto px-4 py-12">
        <Link 
          to="/" 
          className="inline-flex items-center text-primary-600 hover:text-primary-700 mb-8"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Home
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h1 className="text-4xl font-bold text-gray-900 mb-6">System Status</h1>
          
          <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-8">
            <div className="flex items-center">
              <CheckCircle className="w-8 h-8 text-green-500 mr-3" />
              <div>
                <h2 className="text-2xl font-semibold text-green-800">All Systems Operational</h2>
                <p className="text-green-600">All services are running normally</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6 mb-8">
            <h3 className="text-xl font-semibold mb-4 flex items-center">
              <Activity className="w-6 h-6 text-primary-600 mr-2" />
              Service Status
            </h3>
            <div className="space-y-3">
              {services.map((service, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                >
                  <div className="flex items-center">
                    {getStatusIcon(service.status)}
                    <span className="ml-3 font-medium">{service.name}</span>
                  </div>
                  <div className="flex items-center space-x-4">
                    <span className={`text-sm font-medium ${getStatusColor(service.status)}`}>
                      {service.status.charAt(0).toUpperCase() + service.status.slice(1)}
                    </span>
                    <span className="text-sm text-gray-500">
                      {service.uptime} uptime
                    </span>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-xl font-semibold mb-4">Recent Incidents</h3>
            <div className="space-y-4">
              {incidents.map((incident, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="border-l-4 border-gray-200 pl-4"
                >
                  <div className="flex items-center justify-between mb-1">
                    <h4 className="font-semibold">{incident.title}</h4>
                    <span className="text-sm text-gray-500">{incident.date}</span>
                  </div>
                  <p className="text-gray-600 text-sm mb-1">{incident.description}</p>
                  <span className="inline-block px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">
                    {incident.status}
                  </span>
                </motion.div>
              ))}
            </div>
          </div>

          <div className="mt-8 text-center text-gray-600">
            <p>Last updated: {new Date().toLocaleString()}</p>
            <p className="mt-2">For real-time updates, follow <Link to="/contact" className="text-primary-600 hover:underline">@TaskFlowStatus</Link></p>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default StatusPage;
